/* eslint-disable react-refresh/only-export-components */
import { useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

const overlayStack = []
let modalCount = 0
let bodyOverflow = ''
let backgroundSnapshot = []

const focusableSelector = [
  'a[href]', 'button:not(:disabled)', 'input:not(:disabled)', 'select:not(:disabled)',
  'textarea:not(:disabled)', '[tabindex]:not([tabindex="-1"])', '[contenteditable="true"]',
].join(',')

function portalRoot() {
  let root = document.querySelector('[data-wf-overlay-root]')
  if (!root) {
    root = document.createElement('div')
    root.dataset.wfOverlayRoot = ''
    document.body.append(root)
  }
  return root
}

function isolateBackground(root) {
  backgroundSnapshot = [...document.body.children]
    .filter((element) => element !== root)
    .map((element) => ({
      element,
      inert: element.inert,
      ariaHidden: element.getAttribute('aria-hidden'),
    }))

  for (const { element } of backgroundSnapshot) {
    element.inert = true
    element.setAttribute('aria-hidden', 'true')
  }
}

function restoreBackground() {
  for (const { element, inert, ariaHidden } of backgroundSnapshot) {
    element.inert = inert
    if (ariaHidden == null) element.removeAttribute('aria-hidden')
    else element.setAttribute('aria-hidden', ariaHidden)
  }
  backgroundSnapshot = []
}

function setParentIsolation(entry, isolated) {
  if (!entry?.surface) return
  entry.surface.inert = isolated
  if (isolated) entry.surface.setAttribute('aria-hidden', 'true')
  else entry.surface.removeAttribute('aria-hidden')
}

function recomputeParentIsolation() {
  let seenModal = false
  for (let index = overlayStack.length - 1; index >= 0; index -= 1) {
    const entry = overlayStack[index]
    setParentIsolation(entry, seenModal)
    if (entry.modal) seenModal = true
  }
}

// Focuses the first candidate that takes focus: a removed, disabled, hidden
// or inert element refuses it, and the next one is tried (#1054).
function focusFirst(candidates) {
  for (const element of candidates) {
    if (!element?.isConnected) continue
    element.focus()
    if (document.activeElement === element) return element
  }
  return null
}

function initialFocusCandidates(surface, initialFocusRef) {
  return [initialFocusRef?.current, surface?.querySelector('[data-autofocus]'), surface?.querySelector(focusableSelector), surface]
}

// Where focus goes when an overlay closes (#1054): the element it came from;
// when the overlay's own action removed or disabled that (a deleted row, a
// card taken off a surface), the consumer's fallback, unless an overlay still
// open underneath covers it; then that overlay; then the page's main region.
// Never the body: a keyboard user would start over from the top of the page.
function restoreFocus(returnTarget, fallback) {
  const parent = overlayStack.at(-1)?.surface
  const covered = Boolean(parent?.isConnected && fallback && !parent.contains(fallback))
  focusFirst([returnTarget, covered ? null : fallback, parent, document.querySelector('[data-wf-focus-fallback]')])
}

export function useOverlayLifecycle({
  open,
  onClose,
  dismissable = true,
  modal = false,
  trapFocus = false,
  surfaceRef,
  initialFocusRef,
  returnFocusRef,
  returnFocusFallbackRef,
  preserveFocusRef,
}) {
  const entryRef = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  // Read when needed, like onClose: a dialog that turns busy (dismissable
  // off) or moves its first control between steps keeps the lifecycle it
  // opened with, and with it the element focus goes back to (#1054).
  const dismissableRef = useRef(dismissable)
  dismissableRef.current = dismissable
  const initialFocusHolderRef = useRef(initialFocusRef)
  initialFocusHolderRef.current = initialFocusRef
  const fallbackHolderRef = useRef(returnFocusFallbackRef)
  fallbackHolderRef.current = returnFocusFallbackRef

  useLayoutEffect(() => {
    if (!open) return undefined

    const root = portalRoot()
    // The body is where focus lands when the opener was removed or disabled
    // before the overlay opened (or a click never focused it): not a place
    // to go back to, whether it came from returnFocusRef or the page.
    const opener = (element) => (element && element !== document.body ? element : null)
    const returnTarget = opener(returnFocusRef?.current) || opener(document.activeElement)
    const entry = { id: Symbol('overlay'), surface: surfaceRef.current, modal }
    entryRef.current = entry
    overlayStack.push(entry)
    recomputeParentIsolation()

    if (modal) {
      if (modalCount === 0) {
        bodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        isolateBackground(root)
      }
      modalCount += 1
    }

    window.queueMicrotask(() => {
      if (entryRef.current?.id !== entry.id || preserveFocusRef?.current) return
      focusFirst(initialFocusCandidates(surfaceRef.current, initialFocusHolderRef.current))
    })

    const handleKeyDown = (event) => {
      if (overlayStack.at(-1)?.id !== entry.id) return
      if (event.key === 'Escape' && dismissableRef.current) {
        event.preventDefault()
        closeRef.current?.()
        return
      }
      if (event.key !== 'Tab' || !trapFocus) return

      const surface = surfaceRef.current
      const focusable = [...(surface?.querySelectorAll(focusableSelector) || [])]
        .filter((node) => !node.hidden && node.getAttribute('aria-hidden') !== 'true')
      if (!focusable.length) {
        event.preventDefault()
        surface?.focus()
        return
      }
      const first = focusable[0]
      const last = focusable.at(-1)
      if (event.shiftKey && (document.activeElement === first || !surface.contains(document.activeElement))) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    const focusPreservation = preserveFocusRef
    return () => {
      if (entryRef.current?.id === entry.id) entryRef.current = null
      window.removeEventListener('keydown', handleKeyDown, true)
      const index = overlayStack.findIndex((candidate) => candidate.id === entry.id)
      if (index >= 0) overlayStack.splice(index, 1)
      recomputeParentIsolation()

      if (modal) {
        modalCount = Math.max(0, modalCount - 1)
        if (modalCount === 0) {
          document.body.style.overflow = bodyOverflow
          restoreBackground()
        }
      }

      const shouldRestoreFocus = !focusPreservation?.current
      window.queueMicrotask(() => {
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- Cleanup needs the latest fallback if it changed while open.
        if (shouldRestoreFocus) restoreFocus(returnTarget, fallbackHolderRef.current?.current)
      })
    }
  }, [modal, open, preserveFocusRef, returnFocusRef, surfaceRef, trapFocus])

  // A dialog that was busy disables the control that was pressed, and the
  // browser then moves focus to the body. When it turns dismissable again and
  // stays open (the request failed), focus comes back inside it, as it did
  // when a busy change restarted the overlay (#1054).
  useEffect(() => {
    const entry = entryRef.current
    if (!open || !entry || preserveFocusRef?.current || overlayStack.at(-1)?.id !== entry.id) return
    const surface = surfaceRef.current
    if (!surface || surface.contains(document.activeElement)) return
    focusFirst(initialFocusCandidates(surface, initialFocusHolderRef.current))
  }, [dismissable, open, preserveFocusRef, surfaceRef])
}

export function OverlayPortal({ children }) {
  if (typeof document === 'undefined') return null
  return createPortal(children, portalRoot())
}

export { focusableSelector }
